import { createApi } from '@reduxjs/toolkit/query/react'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { Comment, ContactChange, MeetingNote, MeetingNoteFormat, Task, TaskStatus } from '../../models/MeetingSupport'
import { paginate } from '../utils'
import { SyncStatus } from '../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/models'

// Define a service using a base URL and expected endpoints
const _BASE_URL = import.meta.env.REACT_APP_MEETING_SUPPORT_BASE_URL!

export interface GetMeetingNotesRequest extends BasePageRequest {
  roomId: string
  sessionId: string
}

export interface CreateMeetingNoteRequest extends BaseRequest {
  roomId: string
  sessionId: string
  payload: CreateMeetingNotePayload
}

export interface CreateMeetingNotePayload {
  content: string
  format?: MeetingNoteFormat
}

export interface UpdateMeetingNoteRequest extends BaseRequest {
  roomId: string
  sessionId: string
  meetingNoteId: string
  payload: CreateMeetingNotePayload
}

export interface GetCommentsRequest extends BasePageRequest {
  roomId: string
  sessionId: string
}

export interface CreateCommentRequest extends BaseRequest {
  roomId: string
  sessionId: string
  payload: CreateCommentPayload
}

export interface CreateCommentPayload {
  comment: string
  relationId: string
  relationType: string
  relationMetadata?: string
}

export interface UpdateCommentRequest extends BaseRequest {
  roomId: string
  sessionId: string
  commentId: string
  payload: UpdateCommentPayload
}

export interface UpdateCommentPayload {
  comment: string
  relationMetadata?: string
}

export interface DeleteCommentRequest extends BaseRequest {
  roomId: string
  sessionId: string
  commentId: string
}

export interface GetTasksFilters {
  type?: string
  roomId?: string
  sessionId?: string
  emailConnector?: string
  threadId?: string
  emailId?: string
  assignedTo?: string
  status?: string
  relatedTo?: string
}

export interface GetAllTasksRequest extends BaseRequest {
  filters: GetTasksFilters
}

export interface GetTasksRequest extends BasePageRequest {
  filters: GetTasksFilters
}

export interface CreateTaskBatchRequest extends BaseRequest {
  payload: CreateTaskPayload[]
}

export interface Identity {
  type: string
  id: string
}

export interface CreateTaskPayload {
  task: string
  description?: string
  assignedTo: Identity
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: { [key: string]: any }
  type: string
  roomId?: string
  sessionId?: string
  emailConnector?: string
  threadId?: string
  emailId?: string
}

export interface UpdateTaskRequest extends BaseRequest {
  taskId: string
  payload: UpdateTaskPayload
}

export interface UpdateTaskPayload {
  status?: TaskStatus
  task?: string
  description?: string
  metadata?: { [key: string]: string }
}

export interface DeleteTaskRequest extends BaseRequest {
  taskId: string
}

export interface ContactChangeFilterParams {
  type?: string
  typeKey?: string
  userId?: string
}

export interface GetAllContactChangesRequest extends BaseRequest {
  filters: ContactChangeFilterParams
}

export interface GetContactChangeRequest extends BasePageRequest {
  filters: ContactChangeFilterParams
}

export interface CreateContactChangeRequest extends BaseRequest {
  payload: CreateContactChangePayload
}

export interface CreateContactChangePayload {
  type: string
  userId: string
  serviceId: string
  externalId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  previousContactValues: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactValues: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactSchema: { [key: string]: any }
  roomId?: string
  sessionId?: string
  emailConnector?: string
  threadId?: string
}

export interface UpdateContactChangeRequest extends BaseRequest {
  contactChangeId: string
  payload: UpdateContactChangePayload
}

export interface UpdateContactChangePayload {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  previousContactValues?: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactValues?: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactSchema?: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: { [key: string]: any }
}

export interface DeleteContactChangeRequest extends BaseRequest {
  contactChangeId: string
}

export interface GetContactChangeSyncStatusesRequest extends BaseRequest {
  contactChangeId: string
}

export const meetingSupportApi = createApi({
  reducerPath: 'meetingSupportApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['MeetingNotes', 'Comments', 'Tasks', 'ContactChanges'],
  endpoints: (builder) => ({
    getMeetingNotes: builder.query<MeetingNote[], GetMeetingNotesRequest>({
      query: (request) => {
        const { accountId, roomId, sessionId, pageRequest } = request
        return {
          url: `/accounts/${accountId}/rooms/${roomId}/sessions/${sessionId}/notes`,
          params: {
            ...pageRequest,
          },
        }
      },
      transformResponse(response: BasePageResponse<MeetingNote[]>) {
        return response.data
      },
      providesTags: ['MeetingNotes'],
    }),
    createMeetingNote: builder.mutation<BaseResponse<MeetingNote>, CreateMeetingNoteRequest>({
      query: (request) => {
        const { accountId, roomId, sessionId, payload } = request
        return {
          url: `/accounts/${accountId}/rooms/${roomId}/sessions/${sessionId}/notes`,
          method: 'POST',
          body: payload,
        }
      },
      invalidatesTags: ['MeetingNotes'],
    }),
    updateMeetingNote: builder.mutation<BaseResponse<MeetingNote>, UpdateMeetingNoteRequest>({
      query: (request) => {
        const { accountId, roomId, sessionId, meetingNoteId, payload } = request
        return {
          url: `/accounts/${accountId}/rooms/${roomId}/sessions/${sessionId}/notes/${meetingNoteId}`,
          method: 'PATCH',
          body: payload,
        }
      },
      invalidatesTags: ['MeetingNotes'],
    }),
    getComments: builder.query<Comment[], GetCommentsRequest>({
      query: (request) => {
        const { accountId, roomId, sessionId, pageRequest } = request
        return {
          url: `/accounts/${accountId}/rooms/${roomId}/sessions/${sessionId}/comments`,
          params: {
            ...pageRequest,
          },
        }
      },
      transformResponse(response: BasePageResponse<Comment[]>) {
        return response.data.map(parseCommentMetadata)
      },
      providesTags: ['Comments'],
    }),
    createComment: builder.mutation<Comment, CreateCommentRequest>({
      query: (request) => {
        const { accountId, roomId, sessionId, payload } = request
        return {
          url: `/accounts/${accountId}/rooms/${roomId}/sessions/${sessionId}/comments`,
          method: 'POST',
          body: payload,
        }
      },
      transformResponse(response: BaseResponse<Comment>) {
        return parseCommentMetadata(response.data)
      },
      invalidatesTags: ['Comments'],
    }),
    updateComment: builder.mutation<Comment, UpdateCommentRequest>({
      query: (request) => {
        const { accountId, roomId, sessionId, commentId, payload } = request
        return {
          url: `/accounts/${accountId}/rooms/${roomId}/sessions/${sessionId}/comments/${commentId}`,
          method: 'PATCH',
          body: payload,
        }
      },
      transformResponse(response: BaseResponse<Comment>) {
        return parseCommentMetadata(response.data)
      },
      invalidatesTags: ['Comments'],
    }),
    deleteComment: builder.mutation<null, DeleteCommentRequest>({
      query: (request) => {
        const { accountId, roomId, sessionId, commentId } = request
        return {
          url: `/accounts/${accountId}/rooms/${roomId}/sessions/${sessionId}/comments/${commentId}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: ['Comments'],
    }),
    getAllTasks: builder.query<BaseResponse<Task[]>, GetAllTasksRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        const { accountId, filters } = request
        return await paginate<Task>(
          {
            url: `/accounts/${accountId}/tasks`,
            params: {
              ...filters,
            },
          },
          fetchWithBQ,
        )
      },
      providesTags: ['Tasks'],
    }),
    getTasks: builder.query<Task[], GetTasksRequest>({
      query: (request) => {
        const { accountId, filters, pageRequest } = request
        return {
          url: `/accounts/${accountId}/tasks`,
          params: {
            ...filters,
            ...pageRequest,
          },
        }
      },
      transformResponse(response: BasePageResponse<Task[]>) {
        return response.data
      },
      providesTags: ['Tasks'],
    }),
    createTasksBatch: builder.mutation<Task[], CreateTaskBatchRequest>({
      query: (request) => {
        const { accountId, payload } = request
        return {
          url: `/accounts/${accountId}/tasks/batch`,
          method: 'POST',
          body: payload,
        }
      },
      transformResponse(response: BaseResponse<Task[]>) {
        return response.data
      },
      invalidatesTags: ['Tasks'],
    }),
    updateTask: builder.mutation<Task, UpdateTaskRequest>({
      query: (request) => {
        const { accountId, taskId, payload } = request
        return {
          url: `/accounts/${accountId}/tasks/${taskId}`,
          method: 'PATCH',
          body: payload,
        }
      },
      transformResponse(response: BaseResponse<Task>) {
        return response.data
      },
      invalidatesTags: ['Tasks'],
    }),
    deleteTask: builder.mutation<null, DeleteTaskRequest>({
      query: (request) => {
        const { accountId, taskId } = request
        return {
          url: `/accounts/${accountId}/tasks/${taskId}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: ['Tasks'],
    }),
    getContactChanges: builder.query<ContactChange[], GetContactChangeRequest>({
      query: (request) => {
        const { accountId, filters, pageRequest } = request
        return {
          url: `/accounts/${accountId}/contact-changes`,
          params: {
            ...filters,
            ...pageRequest,
          },
        }
      },
      transformResponse(response: BasePageResponse<ContactChange[]>) {
        return response.data
      },
      providesTags: ['ContactChanges'],
    }),
    getAllContactChanges: builder.query<BaseResponse<ContactChange[]>, GetAllContactChangesRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        const { accountId, filters } = request
        return await paginate<ContactChange>(
          {
            url: `/accounts/${accountId}/contact-changes`,
            params: filters,
          },
          fetchWithBQ,
        )
      },
      providesTags: ['ContactChanges'],
    }),
    createContactChange: builder.mutation<ContactChange, CreateContactChangeRequest>({
      query: (request) => {
        const { accountId, payload } = request
        return {
          url: `/accounts/${accountId}/contact-changes`,
          method: 'POST',
          body: payload,
        }
      },
      transformResponse(response: BaseResponse<ContactChange>) {
        return response.data
      },
      invalidatesTags: ['ContactChanges'],
    }),
    updateContactChange: builder.mutation<ContactChange, UpdateContactChangeRequest>({
      query: (request) => {
        const { accountId, contactChangeId, payload } = request
        return {
          url: `/accounts/${accountId}/contact-changes/${contactChangeId}`,
          method: 'PATCH',
          body: payload,
        }
      },
      transformResponse(response: BaseResponse<ContactChange>) {
        return response.data
      },
      invalidatesTags: ['ContactChanges'],
    }),
    deleteContactChange: builder.mutation<null, DeleteContactChangeRequest>({
      query: (request) => {
        const { accountId, contactChangeId } = request
        return {
          url: `/accounts/${accountId}/contact-changes/${contactChangeId}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: ['ContactChanges'],
    }),
    getContactChangeSyncStatuses: builder.query<SyncStatus[], GetContactChangeSyncStatusesRequest>({
      query: ({ accountId, contactChangeId }) => ({
        url: `/accounts/${accountId}/contact-changes/${contactChangeId}/sync-statuses`,
      }),
      providesTags: ['ContactChanges'],
    }),
  }),
})

const parseCommentMetadata = (comment: Comment): Comment => {
  const { quote = '', path = '', basePath = '$', markId = '', deleted = false } = parseMetadata(comment.relationMetadata)
  return {
    ...comment,
    metadata: {
      quote,
      basePath,
      path,
      markId,
      deleted,
    },
  }
}

const parseMetadata = (metadata: string) => {
  if (!metadata) {
    return {}
  }
  try {
    return JSON.parse(metadata)
  } catch {
    console.debug(`Could not parse session metadata: ${metadata}`)
  }
  return {}
}

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetMeetingNotesQuery,
  useLazyGetMeetingNotesQuery,
  useCreateMeetingNoteMutation,
  useUpdateMeetingNoteMutation,
  useGetCommentsQuery,
  useLazyGetCommentsQuery,
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useGetContactChangesQuery,
  useLazyGetContactChangesQuery,
  useCreateContactChangeMutation,
  useUpdateContactChangeMutation,
  useDeleteContactChangeMutation,
  useLazyGetAllContactChangesQuery,
  useLazyGetContactChangeSyncStatusesQuery,
  useLazyGetAllTasksQuery,
  useLazyGetTasksQuery,
  useCreateTasksBatchMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
} = meetingSupportApi
